import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { CurrencyDollar, Check, Lightning, ArrowRight } from "@phosphor-icons/react";
import { usePaymentConfig, getPaymentOptionsForCurrency } from "@/hooks/usePaymentConfig";
import { useFxRates } from "@/hooks/useFxRates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function makeTxRef() {
  const r = (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID() : Math.random().toString(36).slice(2)).replace(/-/g, "");
  return `sub-${r}`;
}

export default function SubscribePlans({ user }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/plans`)
      .then((r) => setPlans(r.data.plans || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-stone-500 text-sm">Loading plans…</p>;
  if (!plans.length) return <p className="text-stone-500 text-sm italic">No paid plans available yet.</p>;

  return (
    <div data-testid="subscribe-plans" className="space-y-6">
      <div>
        <h3 className="font-heading text-lg sm:text-xl font-semibold text-stone-900 mb-1">Upgrade Your API Access</h3>
        <p className="text-sm text-stone-500">
          Free tier includes <strong>1,000 requests/month</strong>. Subscribe for higher quotas, priority support, and SLA guarantees.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} onSelect={() => setSelected(p)} />
        ))}
      </div>

      {selected && (
        <SubscribeModal plan={selected} user={user} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function PlanCard({ plan, onSelect }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 hover:border-emerald-400 hover:-translate-y-0.5 transition-all" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Lightning size={16} weight="fill" className="text-amber-500" />
        <span className="font-heading text-base font-semibold text-stone-900">{plan.name}</span>
      </div>
      <div className="flex items-baseline gap-1 mb-4">
        <span className="font-heading text-3xl font-light text-stone-900">${Number(plan.amount).toFixed(0)}</span>
        <span className="text-xs text-stone-500">/ {plan.billing_period || "month"} USD</span>
      </div>
      <ul className="text-xs text-stone-600 space-y-1.5 mb-5">
        <li className="flex items-start gap-1.5"><Check size={12} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" /> {Number(plan.quota || 0).toLocaleString()} requests / month</li>
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5"><Check size={12} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      <button
        onClick={onSelect}
        data-testid={`plan-select-${plan.id}`}
        className="w-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium py-2.5 rounded-full flex items-center justify-center gap-1.5"
      >
        Subscribe <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function SubscribeModal({ plan, user, onClose }) {
  const { config } = usePaymentConfig();
  const { convert, loaded: fxLoaded } = useFxRates();
  const [currency, setCurrency] = useState("");
  const [stage, setStage] = useState("form");
  const [error, setError] = useState("");
  const txRef = useRef(makeTxRef()).current;

  useEffect(() => {
    if (!currency && config?.currencies?.length) setCurrency(config.currencies[0]);
  }, [config?.currencies, currency]);

  const usdAmount = Number(plan.amount || 0);
  const chargedAmount = useMemo(() => {
    if (!fxLoaded || !currency || currency === "USD") return usdAmount;
    return convert(usdAmount, "USD", currency);
  }, [usdAmount, currency, fxLoaded, convert]);

  const filteredOptions = useMemo(
    () => getPaymentOptionsForCurrency(config, currency),
    [config, currency],
  );

  const flwConfig = useMemo(() => ({
    public_key: config?.public_key || "",
    tx_ref: txRef,
    amount: chargedAmount,
    currency: currency || "USD",
    payment_options: filteredOptions || "card",
    customer: {
      email: user?.email || "subscriber@conexusfoundation.io",
      name: user?.name || "Developer",
      phone_number: "",
    },
    customizations: {
      title: `Conexus Foundation API · ${plan.name}`,
      description: `${Number(plan.quota || 0).toLocaleString()} req/month`,
      logo: "",
    },
  }), [config, txRef, chargedAmount, currency, filteredOptions, user, plan]);

  const handleFlw = useFlutterwave(flwConfig);

  const start = () => {
    setError("");
    if (!user) return setError("Please sign in to subscribe.");
    if (!config?.public_key) return setError("Payment provider not configured.");
    if (!filteredOptions) return setError(`No payment method enabled for ${currency}. Pick another currency.`);
    setStage("initing");
    axios.post(`${API}/payments/subscribe/init/${plan.id}`,
      { tx_ref: txRef, currency }, { withCredentials: true })
      .then(() => {
        handleFlw({
          callback: async (payment) => {
            closePaymentModal();
            try {
              const v = await axios.post(`${API}/payments/subscribe/verify`,
                { tx_ref: txRef, transaction_id: payment.transaction_id }, { withCredentials: true });
              setStage(v.data.status === "successful" ? "success" : "failed");
            } catch { setStage("failed"); }
          },
          onClose: () => setStage("form"),
        });
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Could not start payment");
        setStage("form");
      });
  };

  return (
    <div className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" data-testid="subscribe-modal">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-md w-full overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center gap-3 p-4 sm:p-5 border-b border-stone-200">
          <CurrencyDollar size={20} weight="duotone" className="text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-base sm:text-lg font-semibold text-stone-900 truncate">Subscribe to {plan.name}</h2>
            <p className="text-[11px] text-stone-500">{Number(plan.quota || 0).toLocaleString()} req/month</p>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900 text-2xl leading-none" data-testid="subscribe-close">×</button>
        </header>

        <div className="p-4 sm:p-6 space-y-4">
          {stage === "success" ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Check size={24} weight="bold" className="text-emerald-700" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-stone-900 mb-1">Subscribed!</h3>
              <p className="text-sm text-stone-500 mb-4">Your API quota has been upgraded.</p>
              <button onClick={onClose} className="text-sm text-emerald-700 hover:text-emerald-900">Close</button>
            </div>
          ) : (
            <>
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Base price</p>
                <p className="font-heading text-2xl font-light text-stone-900">US${usdAmount.toFixed(2)}<span className="text-xs text-stone-400 ml-1">/ {plan.billing_period || "month"}</span></p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-400 block mb-1">Pay in</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  data-testid="subscribe-currency"
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500">
                  {(config?.currencies || ["USD"]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {fxLoaded && currency && currency !== "USD" && (
                  <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 mt-2" data-testid="subscribe-fx-line">
                    You&apos;ll pay <strong>{currency} {chargedAmount.toLocaleString()}</strong> at live rate · (US${usdAmount} base)
                  </p>
                )}
                {filteredOptions && (
                  <p className="text-[10px] text-stone-500 mt-1.5">Methods: {filteredOptions.split(",").join(" · ")}</p>
                )}
              </div>

              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

              <button
                onClick={start}
                disabled={stage !== "form" || !currency}
                data-testid="subscribe-pay-btn"
                className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-400 text-white text-sm font-medium py-3 rounded-xl flex items-center justify-center gap-2"
              >
                {stage === "initing" ? "Processing…" : <>Subscribe {currency} {chargedAmount.toLocaleString()} <ArrowRight size={14} weight="bold" /></>}
              </button>

              <p className="text-[10px] text-stone-400 text-center">
                Plans priced in US dollars. We convert to your chosen currency at live ECB rate via open.er-api.com.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
